import { isEqual, startCase } from 'lodash-es'
import { Suspense, lazy, useEffect, useState } from 'react'
import { DateRange, PropsBase, PropsRange, type DayPickerProps } from 'react-day-picker'
import 'react-day-picker/style.css'
import { LooseDate, getDayFunction, useDisclosure } from 'utils'
import {
  Box,
  Button,
  DatesDisplay,
  DatesDisplayProps,
  Flex,
  InputContainerProps,
  Menu,
  MenuProps,
} from '.'
import InputContainer from './InputContainer'

export type DatePickerProps = DayPickerProps

const ReactDayPicker = lazy(() => import('react-day-picker').then(m => ({ default: m.DayPicker })))

export default function DatePicker(props: DatePickerProps) {
  return (
    <Suspense fallback={<Box width={props.mode === 'range' ? '452px' : '210px'} height="260px" />}>
      <ReactDayPicker
        animate
        mode="single"
        numberOfMonths={props.mode === 'range' ? 2 : 1}
        {...props}
      />
    </Suspense>
  )
}

export type SingleDateSelectProps = Omit<InputContainerProps, 'onSelect'> &
  BaseSelectProps & {
    onSelect?: (date?: number) => void
    pickerProps?: Omit<DatePickerProps, 'disabled' | 'mode' | 'selected' | 'onSelect' | 'timeZone'>
    selected?: LooseDate
  }

export function SingleDateSelect({
  buttonDisplayProps,
  disabled,
  menuProps,
  onSelect,
  pickerProps,
  selected,
  timeZone = 'UTC',
  ...rest
}: SingleDateSelectProps) {
  return (
    <InputContainer {...rest}>
      <Menu
        disabled={disabled}
        variant="outline"
        buttonLabel={
          <DatesDisplay
            startTime={selected}
            placeholder="Select date"
            timeZone={timeZone}
            {...buttonDisplayProps}
          />
        }
        options={[
          {
            type: 'custom',
            label: (
              <Box p={4}>
                <DatePicker
                  defaultMonth={toDate(selected)}
                  disabled={disabled}
                  mode="single"
                  timeZone={timeZone === 'local' ? undefined : timeZone}
                  selected={toDate(selected, timeZone)}
                  onSelect={date => onSelect?.(toUTC(date, timeZone))}
                  {...pickerProps}
                />
              </Box>
            ),
          },
        ]}
        {...menuProps}
        menuProps={{
          positioning: { placement: 'bottom-start' },
          lazyMount: true,
          ...menuProps?.menuProps,
        }}
      />
    </InputContainer>
  )
}

export type RangeDateSelectProps = Omit<InputContainerProps, 'onSelect'> &
  BaseSelectProps & {
    defaults?: DefaultDateRange[]
    onSelect?: (range: { from?: number; to?: number } | undefined) => any
    pickerProps?: Omit<
      PropsBase & PropsRange,
      'disabled' | 'mode' | 'required' | 'selected' | 'onSelect' | 'timeZone'
    >
    selected?: { from?: LooseDate; to?: LooseDate }
  }

interface BaseSelectProps {
  buttonDisplayProps?: Omit<DatesDisplayProps, 'startTime' | 'endTime'>
  disabled?: boolean
  menuProps?: Omit<MenuProps<any, false>, 'options'>
  /** (default: 'UTC') UTC, local or IANA timezone */
  timeZone?: string
}

export function RangeDateSelect({
  buttonDisplayProps,
  defaults,
  disabled,
  menuProps,
  onSelect,
  pickerProps,
  selected,
  timeZone = 'UTC',
  ...rest
}: RangeDateSelectProps) {
  const [draftRange, setDraftRange] = useState<DateRange>()
  const { onToggle, open, onClose } = useDisclosure()

  const selectedParsed = {
    from: toDate(selected?.from, timeZone),
    to: toDate(selected?.to, timeZone),
  }

  useEffect(() => {
    setDraftRange(selectedParsed)
  }, [selected, timeZone])

  const getDisabledDates = (date: Date) => {
    if (!draftRange?.from || !pickerProps?.max) return false

    const fromDate = new Date(draftRange.from)
    const toDate = new Date(draftRange.from)
    toDate.setDate(fromDate.getDate() + pickerProps.max - 1) // max is inclusive

    return date < fromDate || date > toDate
  }

  function handleDone() {
    if (!isEqual(draftRange, selectedParsed)) {
      if (draftRange) {
        onSelect?.({ from: toUTC(draftRange.from, timeZone), to: toUTC(draftRange.to, timeZone) })
      } else {
        onSelect?.(undefined)
      }
    }
    onClose()
  }

  function handleCancel() {
    setDraftRange(selectedParsed)
    onClose()
  }

  const defaultsWithValues = defaults?.map(d => ({
    value: d,
    range: parseDefaultToDateRange(d, timeZone),
  }))

  return (
    <InputContainer {...rest}>
      <Menu
        variant="outline"
        disabled={disabled}
        buttonLabel={
          <DatesDisplay
            startTime={selected?.from}
            endTime={selected?.to}
            placeholder="Select Dates"
            timeZone={timeZone}
            {...buttonDisplayProps}
          />
        }
        options={[
          {
            type: 'custom',
            label: (
              <Flex p={4} gap={4}>
                {defaultsWithValues?.length && (
                  <Box as="ul" pt="44px">
                    {defaultsWithValues.map(({ value, range }) => {
                      const isSelected = isEqual(draftRange, range)
                      return (
                        <Box
                          as="li"
                          key={value}
                          cursor="pointer"
                          py={1}
                          borderRadius="sm"
                          userSelect="none"
                          px={2}
                          backgroundColor={isSelected ? 'primary.200' : 'transparent'}
                          _hover={{ backgroundColor: isSelected ? undefined : 'gray.100' }}
                          onClick={() => setDraftRange(range)}
                        >
                          {startCase(value)}
                        </Box>
                      )
                    })}
                  </Box>
                )}
                <Flex direction="column" gap={2}>
                  <DatePicker
                    disabled={disabled || getDisabledDates}
                    defaultMonth={draftRange?.from}
                    mode="range"
                    timeZone={timeZone === 'local' ? undefined : timeZone}
                    required={rest.required}
                    selected={draftRange}
                    onSelect={setDraftRange}
                    {...pickerProps}
                  />
                  <Flex justifyContent="flex-end" gap={2}>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!draftRange}
                      onClick={() => {
                        setDraftRange(undefined)
                      }}
                    >
                      Clear
                    </Button>

                    <Button
                      disabled={!draftRange && rest.required}
                      variant="solid"
                      size="sm"
                      onClick={handleDone}
                      palette="primary"
                    >
                      Done
                    </Button>
                  </Flex>
                </Flex>
              </Flex>
            ),
          },
        ]}
        {...menuProps}
        onClose={handleCancel}
        menuProps={{
          positioning: { placement: 'bottom-end' },
          lazyMount: true,
          open,
          onOpenChange: onToggle,
          ...menuProps?.menuProps,
        }}
      />
    </InputContainer>
  )
}

const toDate = (date?: LooseDate, timeZone: string = 'UTC') =>
  date ? getDayFunction(timeZone)(date).startOf('day').toDate() : undefined

const toUTC = (date?: LooseDate, timeZone: string = 'UTC') =>
  date ? getDayFunction(timeZone)(date).startOf('day').valueOf() : undefined

type DefaultDateRange =
  | 'today'
  | 'yesterday'
  | 'this-week'
  | 'last-week'
  | 'this-month'
  | 'last-month'
  | 'this-quarter'
  | 'last-quarter'
  | 'this-year'
  | 'last-year'

function parseDefaultToDateRange(
  defaultValue: DefaultDateRange,
  timeZone: string
): { from: Date; to: Date } {
  const dayFunction = getDayFunction(timeZone)
  const today = dayFunction().startOf('day')

  switch (defaultValue) {
    case 'today':
      return { from: today.toDate(), to: today.toDate() }
    case 'yesterday':
      return { from: today.subtract(1, 'day').toDate(), to: today.subtract(1, 'day').toDate() }
    case 'this-week':
      return {
        from: today.startOf('week').toDate(),
        to: today.endOf('week').startOf('day').toDate(),
      }
    case 'last-week':
      return {
        from: today.subtract(1, 'week').startOf('week').toDate(),
        to: today.subtract(1, 'week').endOf('week').startOf('day').toDate(),
      }
    case 'this-month':
      return {
        from: today.startOf('month').toDate(),
        to: today.endOf('month').startOf('day').toDate(),
      }
    case 'last-month':
      return {
        from: today.subtract(1, 'month').startOf('month').toDate(),
        to: today.subtract(1, 'month').endOf('month').startOf('day').toDate(),
      }
    case 'this-quarter':
      const q = Math.floor(today.month() / 3) * 3
      return {
        from: today.startOf('month').month(q).toDate(),
        to: today
          .startOf('month')
          .month(q + 2)
          .endOf('month')
          .startOf('day')
          .toDate(),
      }
    case 'last-quarter':
      const lq = Math.floor(today.subtract(3, 'month').month() / 3) * 3
      return {
        from: today.subtract(3, 'month').startOf('month').month(lq).toDate(),
        to: today
          .subtract(3, 'month')
          .startOf('month')
          .month(lq + 2)
          .endOf('month')
          .startOf('day')
          .toDate(),
      }
    case 'this-year':
      return {
        from: today.startOf('year').toDate(),
        to: today.endOf('year').startOf('day').toDate(),
      }
    case 'last-year':
      return {
        from: today.subtract(1, 'year').startOf('year').toDate(),
        to: today.subtract(1, 'year').endOf('year').startOf('day').toDate(),
      }
  }
}

const dateStyles = document.createElement('style')
dateStyles.textContent = `
  .rdp-root {
    --rdp-accent-background-color: #3C4BD3;
    --rdp-accent-color: #152087;
    --rdp-day_button-border-radius: 8px;
    --rdp-day_button-height: 30px;
    --rdp-day_button-width: 30px;
    --rdp-day-height: 30px;
    --rdp-day-width: 30px;
    --rdp-range_end-color: white;
    --rdp-range_middle-color: white;
    --rdp-range_start-color: white;
    
    .rdp-day { font-size: 12px; padding: 0; }
    .rdp-month_caption { justify-content: center; }
    .rdp-nav { justify-content: space-between; width: 100%; }
  }
`
document.head.appendChild(dateStyles)
